// Turns a before/after permission-code diff into sentences a clinic admin can
// actually read, so saving a role reports what the role gained or lost rather
// than just "saved".
//
// Paired codes are collapsed back into their page: flipping
// settings_clinic.view -> {view,edit} is one change ("can now edit Clinic
// Settings"), not two unrelated code grants.

import { PermissionCategory } from "../../../../services/roles.service";

export type ChangeKind = "grant" | "revoke" | "level";

export interface PermissionChange {
  kind: ChangeKind;
  text: string;
}

type Level = "none" | "view" | "edit";

interface CodeMeta {
  label:        string;
  groupKey:     string | null;
  accessLevel:  "view" | "edit" | null;
}

function indexCatalog(catalog: PermissionCategory[]) {
  const byCode = new Map<string, CodeMeta>();
  const groupLabels = new Map<string, string>();
  const groupCodes = new Map<string, { view?: string; edit?: string }>();

  for (const category of catalog) {
    for (const p of category.permissions) {
      byCode.set(p.code, {
        label: p.label,
        groupKey: p.group_key,
        accessLevel: p.access_level,
      });
      if (p.group_key && p.access_level) {
        groupLabels.set(p.group_key, p.label);
        const entry = groupCodes.get(p.group_key) ?? {};
        entry[p.access_level] = p.code;
        groupCodes.set(p.group_key, entry);
      }
    }
  }
  return { byCode, groupLabels, groupCodes };
}

function levelOf(codes: Set<string>, view?: string, edit?: string): Level {
  if (edit && codes.has(edit)) return "edit";
  if (view && codes.has(view)) return "view";
  return "none";
}

/** Human-readable changes between two permission-code sets, catalog order. */
export function diffPermissions(
  catalog: PermissionCategory[],
  before: Set<string>,
  after: Set<string>,
): PermissionChange[] {
  const { byCode, groupLabels, groupCodes } = indexCatalog(catalog);
  const changes: PermissionChange[] = [];

  // ── paired pages ──
  for (const [groupKey, { view, edit }] of groupCodes) {
    const label = groupLabels.get(groupKey) ?? groupKey;
    const from = levelOf(before, view, edit);
    const to = levelOf(after, view, edit);
    if (from === to) continue;

    if (to === "none") {
      changes.push({ kind: "revoke", text: `lost access to ${label}` });
    } else if (from === "none") {
      changes.push({
        kind: "grant",
        text: to === "edit" ? `can now edit ${label}` : `can now view ${label}`,
      });
    } else {
      changes.push({
        kind: "level",
        text: to === "edit" ? `can now edit ${label}` : `is now view-only on ${label}`,
      });
    }
  }

  // ── standalone per-action permissions ──
  const paired = new Set<string>();
  for (const { view, edit } of groupCodes.values()) {
    if (view) paired.add(view);
    if (edit) paired.add(edit);
  }
  const describe = (code: string) => byCode.get(code)?.label ?? code;

  for (const code of after) {
    if (paired.has(code) || before.has(code)) continue;
    changes.push({ kind: "grant", text: `gained "${describe(code)}"` });
  }
  for (const code of before) {
    if (paired.has(code) || after.has(code)) continue;
    changes.push({ kind: "revoke", text: `lost "${describe(code)}"` });
  }

  return changes;
}

/**
 * Collapse a change list into at most `maxLines` toast messages.
 *
 * One toast per change reads well for the common case (an admin ticks one or
 * two boxes); a bulk edit would otherwise stack a dozen toasts and bury the
 * result, so past the cap it becomes a single counted summary.
 */
export function summarizeChanges(
  roleName: string,
  changes: PermissionChange[],
  maxLines = 3,
): string[] {
  if (changes.length === 0) return [];
  if (changes.length <= maxLines) {
    return changes.map(c => `${roleName} ${c.text}.`);
  }
  const granted = changes.filter(c => c.kind === "grant").length;
  const revoked = changes.filter(c => c.kind === "revoke").length;
  const changed = changes.filter(c => c.kind === "level").length;

  const parts: string[] = [];
  if (granted) parts.push(`${granted} granted`);
  if (revoked) parts.push(`${revoked} revoked`);
  if (changed) parts.push(`${changed} access level${changed === 1 ? "" : "s"} changed`);
  return [`${roleName}: ${parts.join(", ")}.`];
}
